import findLastIndex from 'lodash/findLastIndex';

import { MESSAGES } from '../../../../constants';
import { a11yFocus, isHTMLElement } from '../../../../util';
import { isString, isUndefined } from '../../../../util/guards';
import { IBotMessage, ILiveChatMessage } from '../../../common/messageCreators';
import { TItem } from '../../../common/reducers/types';

const getMessageElement = (id: string): HTMLElement | null => {
  return document.querySelector<HTMLElement>(`[data-message-id="${id}"]`);
};

const getActiveMessageId = (): string | null => {
  const active = document.activeElement;

  if (isHTMLElement(active)) {
    return active.getAttribute('data-message-id');
  }

  return null;
};

const maybeMessage = (item: TItem): item is IBotMessage => {
  return 'nodeId' in item;
};

const preventScroll = (item: TItem): boolean => {
  return 'preventScroll' in item && item.preventScroll === true;
};

export const getMessageIdToScroll = (items: TItem[], last?: TItem): string | undefined => {
  if (isUndefined(last)) {
    return;
  }
  // find first message with preventScroll after last user message
  const lastUserMessageIndex = findLastIndex(items, (item) => item.type === MESSAGES.USER_MESSAGE);
  const start = lastUserMessageIndex === -1 ? 0 : lastUserMessageIndex;
  const preventScrollItem = items.slice(start).find((item) => preventScroll(item));
  if (preventScrollItem) {
    return preventScrollItem.id;
  }
  // find first message with the same nodeId as last message nodeId
  if (maybeMessage(last)) {
    let i = items.length - 2;
    let item: TItem = last;

    while (i >= 0) {
      const prev = items[i];

      if (maybeMessage(prev) && prev.nodeId === last.nodeId) {
        item = prev;
        i--;
      } else {
        break;
      }
    }

    return item.id;
  }
  return last.id;
};

const onBlurHandler = ({ target }: FocusEvent) => {
  if (isHTMLElement(target)) {
    target.removeAttribute('tabIndex');
    target.removeAttribute('aria-label');
    target.removeEventListener('blur', onBlurHandler);
  }
};

export const setA11yFocus = (items: TItem[], last?: TItem) => {
  if (isUndefined(last) || preventScroll(last) || last.type !== MESSAGES.BOT_MESSAGE) {
    return;
  }

  const message = getMessageElement(last.id);

  if (isHTMLElement(message)) {
    const activeId = getActiveMessageId();

    if (isString(activeId)) {
      const activeItem = items.find((i) => i.id === activeId);

      if (!isUndefined(activeItem) && maybeMessage(activeItem) && activeItem.nodeId === last.nodeId) {
        return;
      }
    }

    message.setAttribute('tabIndex', '-1');
    message.setAttribute('aria-label', 'new message');
    message.addEventListener('blur', onBlurHandler, { once: true });
    a11yFocus(message);
  }
};

export function isElementScrollable(element: HTMLElement): boolean {
  return element.scrollHeight > element.clientHeight;
}

export function isElementScrolledToBottom(element: HTMLElement, tolerance = 0): boolean {
  const minTolerance = 1; // sometimes some 0.5px deviations occur
  return (
    Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) <= minTolerance + tolerance
  );
}

// Checking if an item is first message of a certain type
export function isFirstOfType(items: TItem[], index: number): boolean {
  return index === 0 || items[index - 1].type !== items[index].type;
}

const isAgentMessage = (item: TItem): item is ILiveChatMessage => item.type === MESSAGES.AGENT_MESSAGE;

// Checking if an item is first message of a certain author (either user or bot/agent)
export function isFirstByAuthor(items: TItem[], index: number): boolean {
  // Considering these utility live chat message types to break the visual sequence of bot messages
  const utilityMessageTypes = [
    MESSAGES.CHANGE_MODE,
    MESSAGES.AGENT_STATUS,
    MESSAGES.CHAT_TRANSFERRED,
    MESSAGES.WAITING_FOR_AGENT,
    MESSAGES.CONNECTING_AGENT,
  ];

  if (index === 0) {
    // Always true for the first message
    return true;
  }

  const currentMessage = items[index];
  const prevMessage = items[index - 1];

  if (currentMessage.type === MESSAGES.USER_MESSAGE) {
    // when it is a user message after any non-user message
    return prevMessage.type !== MESSAGES.USER_MESSAGE;
  }

  if (prevMessage.type === MESSAGES.USER_MESSAGE) {
    // when it is ANY non-user message after a user message
    return true;
  }

  if (utilityMessageTypes.includes(prevMessage.type)) {
    // any of the utility messages should break the sequence
    return true;
  }

  if (
    isAgentMessage(currentMessage) &&
    isAgentMessage(prevMessage) &&
    currentMessage.agent.id !== prevMessage.agent.id
  ) {
    // If both messages are agent messages, but agents are different
    return true;
  }

  return false;
}
